<template>
  <div class="chat-header-indicator">
    <v-menu
        :key="menuKey"
        v-model="menu"
        offset-y
        left
        min-width="280"
        content-class="chat-summary-menu-content"
        :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          class="chat-header-button"
          :class="{ 'chat-header-button-alert': shouldAnimate }"
          v-bind="attrs"
          v-on="on"
          @click="fetchSummary"
        >
          <v-badge
            :value="summary.badge_count > 0"
            :content="summary.badge_count"
            color="red"
            overlap
            class="chat-header-badge"
          >
            <v-icon
              :color="summary.badge_count > 0 ? 'primary' : 'grey'"
            >
              mdi-chat-processing
            </v-icon>
          </v-badge>
        </v-btn>
      </template>

      <v-card class="chat-summary-card">
        <v-card-title class="chat-summary-title">
          Customer chat summary
        </v-card-title>

        <v-divider style="margin-top: .5rem; margin-bottom: .5rem;"/>

        <v-list dense class="chat-summary-list">
          <v-list-item class="chat-summary-item" @click="goToChat">
            <v-list-item-icon>
              <v-icon color="orange" class="chat-summary-icon chat-summary-icon-waiting">
                mdi-account-clock
              </v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>
                Waiting chat
              </v-list-item-title>
              <v-list-item-subtitle>
                Customer waiting for operator
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-chip
                small
                :color="summary.waiting_count > 0 ? 'orange' : 'grey lighten-2'"
                :text-color="summary.waiting_count > 0 ? 'white' : 'grey darken-2'"
              >
                {{ summary.waiting_count }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <v-list-item class="chat-summary-item" @click="goToChat">
            <v-list-item-icon>
              <v-icon color="primary" class="chat-summary-icon chat-summary-icon-unread">
                mdi-message-alert
              </v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>
                Unread messages
              </v-list-item-title>
              <v-list-item-subtitle>
                Customer messages received in active chats
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-chip
                small
                :color="summary.active_unread_count > 0 ? 'red' : 'grey lighten-2'"
                :text-color="summary.active_unread_count > 0 ? 'white' : 'grey darken-2'"
              >
                {{ summary.active_unread_count }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>

          <v-list-item class="chat-summary-item" @click="goToChat">
            <v-list-item-icon>
              <v-icon color="blue-grey" class="chat-summary-icon chat-summary-icon-offline">
                mdi-email-outline
              </v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>
                Offline messages
              </v-list-item-title>
              <v-list-item-subtitle>
                Messages received while offline
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-chip
                small
                :color="summary.offline_unread_count > 0 ? 'blue-grey' : 'grey lighten-2'"
                :text-color="summary.offline_unread_count > 0 ? 'white' : 'grey darken-2'"
              >
                {{ summary.offline_unread_count }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-card-actions>
          <v-btn
            small
            text
            color="primary"
            @click="requestNotificationPermission"
          >
            Enable browser notifications
          </v-btn>
          <v-spacer />

          <v-btn
            small
            text
            color="primary"
            @click="goToChat"
          >
            Open chat dashboard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import ChatService from "@/api/chat.service";

export default {
  name: "ChatHeaderIndicator",

  data() {
    return {
        menu: false,
        menuKey: 0,
        pollingInterval: null,
        animationInterval: null,
        shouldAnimate: false,
        summary: {
          waiting_count: 0,
          active_count: 0,
          active_unread_count: 0,
          offline_unread_count: 0,
          badge_count: 0,
        },
        previousWaitingCount: 0,
        previousUnreadCount: 0,
        previousOfflineUnreadCount: 0,
        notificationPermissionAsked: false,
        operatorHeartbeatInterval: null,
        operatorIdleTimeout: null,
        operatorIsIdle: false,
        lastOperatorActivityAt: Date.now(),
    };
  },

  mounted() {
    this.fetchSummary();
    this.startPolling();
    this.startAnimationInterval();
    this.startOperatorAutoOnline();

    window.addEventListener("chat-summary-refresh", this.fetchSummary);
  },

  beforeDestroy() {
    this.stopPolling();
    this.stopAnimationInterval();
    this.menu = false;
    this.stopOperatorHeartbeat();

    window.removeEventListener("chat-summary-refresh", this.fetchSummary);
  },

    watch: {
        $route() {
            this.forceCloseMenu();
        },
    },

  methods: {
    async fetchSummary() {
      try {
        const oldBadgeCount = this.summary.badge_count || 0;
        const response = await ChatService.getSummary();

        if (response.data && response.data.summary) {
          const newSummary = response.data.summary;
          const newBadgeCount = newSummary.badge_count || 0;

          this.handleSummaryNotification(newSummary);

          this.summary = newSummary;

          if (newBadgeCount > oldBadgeCount) {
            this.triggerAlertAnimation();
          }
        }
      } catch (error) {
        console.error("Chat summary fetch failed", error);
      }
    },

    handleSummaryNotification(newSummary) {
      const newWaitingCount = parseInt(newSummary.waiting_count || 0, 10);
      const newUnreadCount = parseInt(newSummary.active_unread_count || 0, 10);
      const newOfflineUnreadCount = parseInt(newSummary.offline_unread_count || 0, 10);

      if (newWaitingCount > this.previousWaitingCount) {
        this.showBrowserNotification(
          "Új várakozó chat",
          "Egy ügyfél várakozik az ügyintézőre."
        );
      }
    
      if (newUnreadCount > this.previousUnreadCount) {
        this.showBrowserNotification(
          "Új chat üzenet",
          "Új olvasatlan ügyfélüzenet érkezett."
        );
      }

      if (newOfflineUnreadCount > this.previousOfflineUnreadCount) {
        this.showBrowserNotification(
          "Új offline chat üzenet",
          "Új olvasatlan offline ügyfélüzenet érkezett."
        );
      }
    
      this.previousWaitingCount = newWaitingCount;
      this.previousUnreadCount = newUnreadCount;
      this.previousOfflineUnreadCount = newOfflineUnreadCount;
    },

    startPolling() {
      this.stopPolling();

      this.pollingInterval = setInterval(() => {
        this.fetchSummary();
      }, 30000);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    goToChat() {
      this.menu = false;

        if (this.$router && this.$route.path !== "/chat/chat") {
            this.$router.push("/chat/chat");
        }
    },

    startAnimationInterval() {
      this.stopAnimationInterval();

      this.animationInterval = setInterval(() => {
        if (this.summary.badge_count > 0) {
          this.triggerAlertAnimation();
        }
      }, 30000);
    },

    stopAnimationInterval() {
      if (this.animationInterval) {
        clearInterval(this.animationInterval);
        this.animationInterval = null;
      }
    },

    triggerAlertAnimation() {
      this.shouldAnimate = false;

      this.$nextTick(() => {
        this.shouldAnimate = true;

        setTimeout(() => {
          this.shouldAnimate = false;
        }, 1800);
      });
    },

    forceCloseMenu() {
        this.menu = false;

        this.$nextTick(() => {
            this.menuKey += 1;

            if (document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
            }
        });
    },

    requestNotificationPermission() {
      if (!("Notification" in window)) {
        return;
      }

      if (Notification.permission === "default") {
        Notification.requestPermission().then((permission) => {
          this.notificationPermissionAsked = true;

          if (permission === "granted") {
            this.showBrowserNotification(
              "Értesítések bekapcsolva",
              "Mostantól jelezzük az új chat eseményeket."
            );
          }
        });
      }
    },

    showBrowserNotification(title, body) {
      if (!("Notification" in window)) {
        return;
      }

      if (Notification.permission !== "granted") {
        return;
      }

      const notification = new Notification(title, {
        body,
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "customer-chat",
        renotify: true,
      });

      notification.onclick = () => {
        window.focus();

        if (window.location.hash !== "#/chat") {
          window.location.href = "#/chat";
        }

        notification.close();
      };
    },
    async startOperatorAutoOnline() {
      if (localStorage.getItem("chat_operator_manual_offline") === "1") {
        return;
      }

      try {
        const response = await ChatService.autoOnline();

        if (response.data.online !== true) {
          return;
        }

        localStorage.setItem("chat_operator_auto_online_active", "1");
        this.startOperatorHeartbeat("auto");
      } catch (error) {
        console.error("Chat operator auto online failed", error);
      }
    },

    startOperatorHeartbeat(mode = "auto") {
      this.stopOperatorHeartbeat();

      this.operatorHeartbeatInterval = window.setInterval(() => {
        if (document.hidden) {
          return;
        }

        ChatService.touchOperator({
          mode,
        }).then((response) => {
          if (mode === "auto" && response.data.online !== true) {
            this.stopOperatorHeartbeat();
            localStorage.removeItem("chat_operator_auto_online_active");

            if (typeof this.fetchSummary === "function") {
              this.fetchSummary();
            }
          }
        }).catch((error) => {
          console.error("Chat operator heartbeat failed", error);
        });
      }, 60000);
    },

    stopOperatorHeartbeat() {
      if (this.operatorHeartbeatInterval) {
        window.clearInterval(this.operatorHeartbeatInterval);
        this.operatorHeartbeatInterval = null;
      }
    },

    async setOperatorOnline() {
      try {
        await ChatService.setOperatorOnline();

        localStorage.removeItem("chat_operator_manual_offline");
        localStorage.removeItem("chat_operator_auto_online_active");
        this.startOperatorHeartbeat("manual");
      } catch (error) {
        console.error("Set operator online failed", error);
      }
    },

    async setOperatorOffline() {
      try {
        await ChatService.setOperatorOffline();

        localStorage.setItem("chat_operator_manual_offline", "1");
        localStorage.removeItem("chat_operator_auto_online_active");
        this.stopOperatorHeartbeat();
      } catch (error) {
        console.error("Set operator offline failed", error);
      }
    },
  },
};
</script>

<style scoped>
.chat-header-indicator {
  display: inline-flex;
  align-items: center;
}

.chat-header-button {
  margin-left: 4px;
}

.chat-summary-title {
  font-size: 16px;
  font-weight: 700;
}

.chat-header-button-alert {
  animation: chatAlertFloat 1.8s ease-in-out 1;
}

.chat-header-button-alert .v-icon {
  animation: chatAlertGlow 1.8s ease-in-out 1;
}

@keyframes chatAlertFloat {
  0% {
    transform: translateY(0) rotate(0deg) scale(1);
  }

  20% {
    transform: translateY(-1px) rotate(-5deg) scale(1.04);
  }

  40% {
    transform: translateY(0) rotate(4deg) scale(1.04);
  }

  60% {
    transform: translateY(-1px) rotate(-3deg) scale(1.03);
  }

  80% {
    transform: translateY(0) rotate(2deg) scale(1.01);
  }

  100% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
}

@keyframes chatAlertGlow {
  0% {
    filter: drop-shadow(0 0 0 rgba(244, 63, 94, 0));
  }

  45% {
    filter: drop-shadow(0 0 7px rgba(244, 63, 94, 0.65));
  }

  100% {
    filter: drop-shadow(0 0 0 rgba(244, 63, 94, 0));
  }
}


@keyframes chatBadgePulse {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, .35);
  }

  70% {
    box-shadow: 0 0 0 7px rgba(244, 67, 54, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}
</style>
<style>
.chat-header-badge .v-badge__badge {
  font-weight: 800;
  animation: none;
  box-shadow: 0 0 0 0 rgba(244, 67, 54, .35);
}

.chat-summary-menu-content {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
}

.chat-summary-menu-content .chat-summary-card {
  width: 360px;
  border-radius: 6px;
}

.chat-summary-menu-content .chat-summary-title {
  min-height: 42px;
  padding: 18px 20px;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
}

.chat-summary-menu-content .chat-summary-item {
  min-height: 64px !important;
  padding: 8px 20px !important;
}

.chat-summary-menu-content .chat-summary-item .v-list-item__icon {
  margin: 0 18px 0 0 !important;
  align-self: center !important;
}

.chat-summary-menu-content .chat-summary-item .v-list-item__content {
  padding: 0 !important;
}

.chat-summary-menu-content .chat-summary-item .v-list-item__title {
  font-size: 14px !important;
  font-weight: 700 !important;
  line-height: 1.3 !important;
  color: #212121 !important;
}

.chat-summary-menu-content .chat-summary-item .v-list-item__subtitle {
  margin-top: 2px !important;
  font-size: 12px !important;
  line-height: 1.35 !important;
  color: #616161 !important;
}

.chat-summary-menu-content .chat-summary-item .v-list-item__action {
  margin: 0 0 0 12px !important;
}

.chat-summary-menu-content .chat-summary-icon-waiting {
  color: #fb8c00 !important;
}

.chat-summary-menu-content .chat-summary-icon-unread {
  color: #303f9f !important;
}

.chat-summary-menu-content .chat-summary-icon-offline {
  color: #607d8b !important;
}
</style>